import type { FC, ReactNode } from 'react';
import { Avatar, Col, Divider, Row, Switch, Typography } from 'antd';
import type { SwitchProps } from 'antd';
import { TbCircle, TbMinusVertical } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface SettingsToggleRowProps {
    checked: boolean;
    description: ReactNode;
    divided?: boolean;
    disabled?: boolean;
    icon?: ReactNode;
    label: ReactNode;
    loading?: boolean;
    onChange: NonNullable<SwitchProps['onChange']>;
    switchAriaLabel: string;
    switchSize?: SwitchProps['size'];
}

const SettingsToggleRow: FC<SettingsToggleRowProps> = ({
    checked,
    description,
    divided = false,
    disabled = false,
    icon,
    label,
    loading = false,
    onChange,
    switchAriaLabel,
    switchSize = 'small',
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <>
            {divided ? <Divider style={{ margin: '0 0 0.75rem' }} /> : null}
            <Row align='middle' gutter={[12, 8]} wrap={false}>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <div style={{ alignItems: 'flex-start', display: 'flex', gap: '0.75rem', minWidth: 0 }}>
                        {icon ? (
                            <Avatar
                                aria-hidden='true'
                                icon={icon}
                                shape='square'
                                size={28}
                                style={{
                                    background: token.colorFillQuaternary,
                                    border: `1px solid ${token.colorBorderSecondary}`,
                                    color: token.colorTextSecondary,
                                    flex: 'none',
                                }}
                            />
                        ) : null}
                        <div style={{ minWidth: 0 }}>
                            <Typography.Title level={5} style={{ marginBottom: '0.125rem' }}>
                                {label}
                            </Typography.Title>
                            <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                                {description}
                            </Typography.Paragraph>
                        </div>
                    </div>
                </Col>
                <Col flex='none'>
                    <Switch
                        aria-label={switchAriaLabel}
                        checked={checked}
                        checkedChildren={<TbMinusVertical aria-hidden='true' style={{ verticalAlign: '-0.1rem' }} />}
                        disabled={disabled}
                        loading={loading}
                        size={switchSize}
                        unCheckedChildren={<TbCircle aria-hidden='true' style={{ verticalAlign: '-0.1rem' }} />}
                        onChange={onChange}
                    />
                </Col>
            </Row>
        </>
    );
};

export default SettingsToggleRow;
